import { Task, TaskStatus, WayPoint } from '@bringg/types';
import { Dictionary } from 'lodash';
import _get from 'lodash/get';
import { NO_ROUTE_ID } from '@bringg-frontend/routes-planner';

const WAYPOINT_DROP_OFF_POSITION = 2;

const watchedTaskEntries = [];

const getTaskEntries = (handlingUnitNames: string[]): string[] => {
	if (watchedTaskEntries.length === 0) {
		Array.prototype.push.apply(watchedTaskEntries, ['user_id', 'status', 'run_id', 'aggregations.max_weight']);
		handlingUnitNames.forEach(unitName => watchedTaskEntries.push(`aggregations.max_handling_units.${unitName}`));
	}
	return watchedTaskEntries;
};

const getWayPointById = (task: Task, wayPointId: number) =>
	task.way_points.find(wayPoint => wayPoint.id === wayPointId);

const isWayPointChangedByTasks = (currentTask: Task, updatedTask: Task) => {
	const currentWayPoint = getRoutesPlannerWayPoint(currentTask);
	const updatedWayPoint = getWayPointById(updatedTask, currentWayPoint.id);

	return isWayPointChanged(currentWayPoint, updatedWayPoint);
};

const isRunExists = (tasks: Task[], updatedTask) => {
	const updatedRunId = updatedTask.run_id;
	return tasks.some(task => {
		return task.run_id === updatedRunId;
	});
};

const isTaskChangedByEntries = (currentTask: Task, updatedTask: Task, entries: string[]) => {
	return entries.some(entry => {
		return _get(currentTask, entry) !== _get(updatedTask, entry);
	});
};

const isTaskChanged = (currentTask: Task, updatedTask: Task, isRunExistsFlag: boolean, handlingUnitNames: string[]) => {
	const entries = getTaskEntries(handlingUnitNames);
	return (
		(currentTask &&
			(isWayPointChangedByTasks(currentTask, updatedTask) ||
				isTaskChangedByEntries(currentTask, updatedTask, entries))) ||
		(!currentTask && isRunExistsFlag)
	);
};

const isWayPointChanged = (currentWayPoint: WayPoint, updatedWayPoint: WayPoint) => {
	const entries = ['eta', 'etl', 'lat', 'lng', 'no_earlier_than', 'no_later_than'];
	return entries.some(entry => currentWayPoint[entry] !== updatedWayPoint[entry]);
};

export const getRoutesPlannerWayPoint = (task: Task) => {
	return task.way_points?.find(wayPoint => wayPoint.position === WAYPOINT_DROP_OFF_POSITION);
};

export const getTaskByWayPointId = (tasksById: Dictionary<Task>, wayPointId: number) => {
	return Object.values(tasksById).find(item => {
		return getRoutesPlannerWayPoint(item).id === wayPointId;
	});
};

export const isTaskEventValid = (
	tasks: Task[],
	currentTask: Task,
	updatedTask: Task,
	handlingUnitNames: string[]
): boolean => {
	const runExists = isRunExists(tasks, updatedTask);
	return (
		!!getRoutesPlannerWayPoint(updatedTask) &&
		!shouldRemoveTask(updatedTask) &&
		isTaskChanged(currentTask, updatedTask, runExists, handlingUnitNames)
	);
};

export const isWayPointEventValid = (tasksById: Dictionary<Task>, wayPoint: WayPoint) => {
	const task = getTaskByWayPointId(tasksById, wayPoint.id);
	if (!task) {
		return false;
	}

	const currentWayPoint = getRoutesPlannerWayPoint(task);
	if (!currentWayPoint) {
		return false;
	}
	return isWayPointChanged(currentWayPoint, wayPoint);
};

export const updateTasksData = (currentTaskData: Task, updatedTaskData: Task) => {
	const updatedTask = updatedTaskData;
	const currentTask = currentTaskData;

	updatedTask.way_points = [getRoutesPlannerWayPoint(updatedTaskData)];

	if (updatedTaskData.aggregations) {
		updatedTask.aggregations = { ...updatedTaskData.aggregations };
	}

	if (currentTask && !currentTask.run_id) {
		currentTask.run_id = NO_ROUTE_ID;
	}
	if (updatedTask && !updatedTask.run_id) {
		updatedTask.run_id = NO_ROUTE_ID;
	}
};

export const updateWayPointData = (tasksByIds: Dictionary<Task>, updatedWayPoint: WayPoint) => {
	const task = tasksByIds[updatedWayPoint.task_id];
	const wayPoint = getRoutesPlannerWayPoint(task);
	Object.assign(wayPoint, updatedWayPoint);
};

export const getRunIdsToUpdate = (currentTask: Task, updatedTask: Task) => {
	const currentRunId = currentTask?.run_id ?? NO_ROUTE_ID;
	const updatedRunId = updatedTask.run_id ?? NO_ROUTE_ID;
	return Array.from(new Set([currentRunId, updatedRunId]));
};

export const shouldRemoveTask = (task: Task) => {
	return task.status === TaskStatus.Done || task.status === TaskStatus.Cancelled;
};
